import { classifyInfraTier, type InfraTier } from '../services/InfraLayoutEngine';

/**
 * Tier palette used by the Compass Explorer. Ingress lands on red so
 * public-facing surface reads instantly; compute/data/shared get
 * distinct colours so a card's mix communicates its function without
 * the reader having to parse any text.
 */
export const TIER_COLORS: Record<InfraTier, string> = {
  ingress: '#ef4444', // ALB/NLB/IGW/NAT/APIGW — red, public surface
  compute: '#3b82f6', // EC2/Lambda/ECS — blue
  data: '#10b981',    // RDS/Dynamo/storage — green
  shared: '#a855f7',  // IAM/KMS/SNS — purple, cross-cutting
  network: '#64748b', // Route tables / NACLs / plumbing — slate
  other: '#94a3b8',   // Fallback — light slate
};

/**
 * Human-readable labels for tier bands. Uppercase-short so the label
 * column stays narrow and dots get the width they deserve.
 */
export const TIER_LABELS: Record<InfraTier, string> = {
  ingress: 'Ingress',
  compute: 'Compute',
  data: 'Data',
  shared: 'Shared',
  network: 'Network',
  other: 'Other',
};

/**
 * Rendering order — ingress first because public surface is what a
 * reviewer scans for; compute and data next because they carry the
 * functional payload; shared (IAM/KMS) and network (SG/route) last
 * because they're usually support infrastructure.
 */
export const TIER_ORDER: readonly InfraTier[] = [
  'ingress', 'compute', 'data', 'shared', 'network', 'other',
];

// Short-form overrides for common types that classifyInfraTier doesn't
// know about (it only keys full `aws_*` manifest ids and misses
// security groups, vpc endpoints, etc. under short form).
const SHORT_FORM_TIER: Record<string, InfraTier> = {
  sg: 'network',
  security_group: 'network',
  vpc_endpoint: 'network',
  vpce: 'network',
  nacl: 'network',
  eni: 'network',
  iam: 'shared',
  role: 'shared',
  policy: 'shared',
  iam_role: 'shared',
  iam_policy: 'shared',
  iam_user: 'shared',
  kms_key: 'shared',
  secret: 'shared',
  ebs: 'data',
  efs: 'data',
  volume: 'data',
  bucket: 'data',
};

/**
 * Classify a short-form resource type (`ec2`, `sg`, `alb`, `iam_role`)
 * into a tier, preferring the bubble-level short-form map. Falls back
 * to the layout engine's `classifyInfraTier` which is keyed on full
 * `aws_*` manifest ids.
 */
export function tierOf(resourceType: string): InfraTier {
  const shortTier = SHORT_FORM_TIER[resourceType];
  if (shortTier) return shortTier;
  const bare = classifyInfraTier(resourceType);
  if (bare !== 'other') return bare;
  return classifyInfraTier(`aws_${resourceType}`);
}

export interface InteriorDotLite {
  id: string;
  resourceType: string;
  label: string;
}

export interface TierBand {
  tier: InfraTier;
  label: string;
  color: string;
  dots: InteriorDotLite[];
}

/**
 * Group an interior dot list into tier bands in display order, with
 * empty bands filtered out. Pure function — safe to call in the scene
 * builder and the renderer.
 */
export function groupByTier(interior: readonly InteriorDotLite[]): TierBand[] {
  const buckets = new Map<InfraTier, InteriorDotLite[]>();
  for (const tier of TIER_ORDER) buckets.set(tier, []);
  for (const dot of interior) {
    const tier = tierOf(dot.resourceType);
    buckets.get(tier)!.push(dot);
  }
  const out: TierBand[] = [];
  for (const tier of TIER_ORDER) {
    const dots = buckets.get(tier)!;
    if (dots.length === 0) continue;
    out.push({ tier, label: TIER_LABELS[tier], color: TIER_COLORS[tier], dots });
  }
  return out;
}

export type { InfraTier };
